import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import Navbar from './Navbar';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Gestion du chargement des pages
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Gestion du scroll vers le haut lors du changement de page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Gestion des raccourcis clavier
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Alt + flèche gauche pour revenir en arrière
      if (e.altKey && e.key === 'ArrowLeft') {
        navigate(-1);
      }
      // Alt + flèche droite pour aller en avant
      if (e.altKey && e.key === 'ArrowRight') {
        navigate(1);
      }
      // Alt + Home pour aller à l'accueil
      if (e.altKey && e.key === 'h') {
        navigate('/');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate]);

  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: -20
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Espace pour compenser la navbar fixe */}
      <div className="h-16"></div>
      
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white bg-opacity-80 z-50 flex items-center justify-center"
          >
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-blue-600 font-medium">Chargement...</p>
            </div>
          </motion.div>
        ) : (
          <motion.main
            key={location.pathname}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
            transition={{ duration: 0.3 }}
            className="container mx-auto px-4 py-8 max-w-7xl"
          >
            <Outlet />
          </motion.main>
        )}
      </AnimatePresence>

      {/* Bouton retour en haut */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: window.scrollY > 200 ? 1 : 0 }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          />
        </svg>
      </motion.button>

      {/* Barre de progression */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-blue-600 origin-left z-50"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: window.scrollY / (document.documentElement.scrollHeight - window.innerHeight) || 0 }}
      />
    </div>
  );
};

export default Layout; 